import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { UserFormComponent } from '../user-form/user-form.component';
import { UserService } from '../../../services/user.service';
import { UpdateUserRequest } from '../../../models/api/updateUserRequest';
import { UserMappingService } from '../../../services/user-mapping.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { forkJoin, switchMap } from 'rxjs';

@Component({
  selector: 'app-user-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UserFormComponent,
    NgxSpinnerModule],
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss']
})
export class UserEditComponent implements OnInit {
  form: FormGroup;
  userId: string | null = null;
  createdDate?: Date;
  updatedDate?: Date;

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private userMappingService: UserMappingService,
    private router: Router,
    private route: ActivatedRoute,
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      fullName: ['', Validators.required],
      role: ['', Validators.required],
      category: [''],
      active: [true],
      selectedCountries: [[]],
      selectedTabs: [[]],
      readonlyTabs: [[]],
      selectedMeasurements: [[]],
      selectedSections: [[]]
    });
  }

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    this.userId = id;
    if (this.userId) {
      this.userService.getUser(this.userId).subscribe({
        next: (user) => {
          if (user) {
            const companyAccess = Array.isArray(user.companyAccess) ? user.companyAccess : [];
            const companyAccessReadOnly = Array.isArray(user.companyAccessReadOnly) ? user.companyAccessReadOnly : [];
            const accessToYears = Array.isArray(user.accessToYears) ? user.accessToYears : [];

            this.createdDate = user.createdDate;
            this.updatedDate = user.updatedDate;

            this.form.patchValue({
              email: user.email,
              fullName: user.fullName,
              role: this.userMappingService.mapRoleToDisplayName(user.role),
              category: this.userMappingService.mapCategoryToDisplayName(user.category),
              active: user.isActive,
              selectedTabs: this.userMappingService.mapEnumsToCompanyAccess(companyAccess),
              readonlyTabs: this.userMappingService.mapEnumsToTabNames(companyAccessReadOnly),
              selectedMeasurements: accessToYears.map(year => year.toString())
            });

            this.userService.getCountryAccess(this.userId!).subscribe({
              next: (response: any) => {
                const countryArray = response?.countries ? response.countries.map((c: any) => c.id) : [];
                this.form.patchValue({ selectedCountries: countryArray });
              },
              error: (error: Error) => console.error('Error loading country access:', error)
            });

            this.userService.getSectionAccess(this.userId!).subscribe({
              next: (response: any) => {
                const sectionsArray = response?.sectionAccess || [];
                this.form.patchValue({ selectedSections: sectionsArray });
              },
              error: (error: Error) => console.error('Error loading section access:', error)
            });
          }
        },
        error: (error: Error) => console.error('Error loading user:', error)
      });
    }
  }

  onSubmit() {
    this.spinnerService.show();
    if (this.form.valid && this.userId) {
      const updateRequest: UpdateUserRequest = {
        email: this.form.value.email,
        fullName: this.form.value.fullName,
        role: this.userMappingService.mapRoleToEnum(this.form.value.role),
        category: this.userMappingService.mapCategoryToEnum(this.form.value.category),
        companyAccess: this.userMappingService.mapCompanyAccessToEnums(this.form.value.selectedTabs || []),
        companyAccessReadOnly: this.userMappingService.mapTabNamesToEnums(this.form.value.readonlyTabs || []),
        accessToYears: (this.form.value.selectedMeasurements || []).map((year: string) => parseInt(year, 10)),
        isActive: this.form.value.active
      };

      this.userService.updateUser(this.userId, updateRequest).pipe(
        switchMap(() => {
          const updateRequests = [];

          updateRequests.push(
            this.userService.updateCountryAccess(this.userId!, {
              countryIds: this.form.value.selectedCountries || []
            })
          );

          updateRequests.push(
            this.userService.updateSectionAccess(this.userId!, {
              sectionAccess: this.form.value.selectedSections || []
            })
          );

          return forkJoin(updateRequests);
        })
      ).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.toast.success('User updated successfully', {
            iconTheme: {
              primary: 'var(--mat-sys-primary)',
              secondary: '#FFF',
            }
          });
          this.router.navigate(['/users']);
        },
        error: (error: Error) => {
          this.spinnerService.hide();
          this.toast.error('Error updating user: ' + error.message, {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
          console.error('Error updating user:', error);
        }
      });
    }
  }

  onCancel() {
    this.router.navigate(['/users']);
  }
}
